import React from "react";
import { makeStyles } from "@material-ui/core/styles";

// Material
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

// Assets
import mainImage from "../../assets/backgrounds/getConnectedPage/policing-bg.png";

// Components
import Section from "../../components/Section/Section";
import Text, { types } from "../../components/Text/Text";
import HighlightColor from "../../components/HighlightColor/index";

// Icons
// import westMerciaPolice from "../../assets/backgrounds/getConnectedPage/westMerciaPolice.svg";
import hertfordshireConstabularyBadge from "../../assets/backgrounds/getConnectedPage/hertfordshireConstabularyBadge.svg";
import staffordshirePolice from "../../assets/backgrounds/getConnectedPage/staffordshirePolice.svg";
import statesOfJersey from "../../assets/backgrounds/getConnectedPage/statesOfJersey.svg";
import colors from "../../config/colors";
import LinkScroll from "../../components/LinkScroll/LinkScroll";
import urls from "../../config/urls";

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: "125px",
    paddingBottom: "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(4),
    backgroundImage: `url(${mainImage})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "1560px",
    backgroundPosition: "47% 35%",
    [theme.breakpoints.down("md")]: {
      gap: "5vw",
      paddingTop: "180px",
      padding: "180px 10% 140px",
      backgroundSize: "90%",
      backgroundPosition: "47% 35%"
    },
    [theme.breakpoints.down("sm")]: {
      backgroundSize: "2200px",
      backgroundPosition: "80% 55%",
      flexDirection: "column-reverse",
      padding: "48px 12px 56px",
      gap: 0
    }
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(4),
    width: "100%",
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(3)
    },
    [theme.breakpoints.down("sm")]: {
      gap: 0
    }
  },
  heading: {
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  kulpa: {
    fontWeight: 800
  },
  cardWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: theme.spacing(3),
    paddingTop: theme.spacing(5),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
      paddingTop: 0,
      gap: theme.spacing(1)
    }
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: 445,
    backgroundColor: "#fff",
    borderRadius: "40px",
    border: "1px solid #E0E0E0",
    boxShadow: "0px 0px 30px #0000000D",
    padding: theme.spacing(6, 4),
    [theme.breakpoints.down("md")]: {},
    [theme.breakpoints.down("sm")]: {
      maxWidth: 600,
      width: "100%",
      padding: "24px 16px",
      alignItems: "center"
    }
  },
  badge: {
    fontFamily: ["Gibson", "sans-serif"].join(","),
    fontSize: "10px",
    backgroundColor: colors.doveGray,
    color: "#fff",
    borderRadius: "4px",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down("sm")]: {
      marginLeft: "auto",
      marginBottom: "32px"
    }
  },
  titleCard: {
    fontSize: "112px",
    color: colors.mineShaft,
    fontWeight: 900,
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    [theme.breakpoints.down("md")]: {
      fontSize: "80px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "64px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "70px"
    }
  },
  subtitleCard: {
    color: colors.mineShaft,
    fontSize: "30px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      fontSize: "24px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "22px"
    },
    [theme.breakpoints.down("sm")]: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      textAlign: "center"
    }
  },
  descriptionCard: {
    color: colors.mineShaft,
    fontSize: "20px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    [theme.breakpoints.down("md")]: {
      fontSize: "18px"
    },
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  titleLabel: {
    color: colors.mineShaft,
    fontSize: "20px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    marginBottom: "33px",
    marginTop: "40px",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      margin: "36px 0 20px"
    }
  },
  spanCard: { fontWeight: 700 },
  imgLogo: {
    display: "flex",
    marginBottom: "70px",
    justifyContent: "center",
    gap: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
      marginBottom: "28px"
    }
  },
  forceLogo: {
    [theme.breakpoints.down("sm")]: {
      width: "66px"
    }
  },
  button: {
    fontFamily: "Montserrat Black",
    textTransform: "none",
    fontSize: "24px",
    padding: "28px 124px",
    borderRadius: "10px",
    boxShadow: "0px 3px 66px #00000029",
    backgroundColor: "#0B4283",
    color: "#fff",
    textAlign: "center",
    lineHeight: 1,
    maxWidth: "948px",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
      padding: "24px 90px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
      marginBottom: theme.spacing(4),
      padding: "24px 36px",
      width: "100%",
      maxWidth: "600px",
      lineHeight: 1.5
    }
  }
}));

const GetConnectedPolicing = () => {
  const classes = useStyles();

  return (
    <Section className={classes.root}>
      <div className={classes.contentWrapper}>
        <Text type={types.sectionTitle} className={classes.title}>
          <HighlightColor className={classes.kulpa}>Kulpa</HighlightColor> For
          Policing
        </Text>
        <Text type={types.titleDescription} className={classes.heading}>
          This is what connecting to kulpa could achieve
        </Text>
        <div className={classes.cardWrapper}>
          <Paper className={classes.card}>
            <Typography className={classes.badge}>BETTER OUTCOMES</Typography>
            <Typography className={classes.titleCard}>2-3X</Typography>
            <Typography className={classes.subtitleCard}>
              More positive outcomes
            </Typography>
            <Typography className={classes.descriptionCard}>
              Outcomes (1 Charge/Summons, 2 Youth Caution, 3 Adult Caution, 8 Community Resolution, 22 Diversionary,
              Educational or Intervention Activity)
            </Typography>
          </Paper>
          <Paper className={classes.card}>
            <Typography className={classes.badge}>MORE EFFICIENT</Typography>
            <Typography className={classes.titleCard}>-50%</Typography>
            <Typography className={classes.subtitleCard}>
              Fewer phone downloads
            </Typography>
            <Typography className={classes.descriptionCard}>
              Mobile devices seized and downloaded via a KIOSK or submitted to
              the Digital Forensic Unit (DFU)
            </Typography>
          </Paper>
          <Paper className={classes.card}>
            <Typography className={classes.badge}>MORE EFFECTIVE</Typography>
            <Typography className={classes.titleCard}>-25%</Typography>
            <Typography className={classes.subtitleCard}>
              Reduction in investigation times
            </Typography>
            <Typography className={classes.descriptionCard}>
              Average time between opening an investigation and recording an
              outcome
            </Typography>
          </Paper>
        </div>
        <div className={classes.label}>
          <Typography className={classes.titleLabel}>
            Some of the forces already connected
          </Typography>
          <div className={classes.imgLogo}>
            <img
              src={hertfordshireConstabularyBadge}
              alt="Hertfordshire Constabulary Badge"
              className={classes.forceLogo}
            />
            <img
              src={statesOfJersey}
              alt="States Of Jersey"
              className={classes.forceLogo}
            />
            <img
              src={staffordshirePolice}
              alt="Staffordshire Police"
              className={classes.forceLogo}
            />
            {/*
              Remove this comment to show West Mercia Police logo
              <img
                src={westMerciaPolice}
                alt="West Mercia Police"
                className={classes.forceLogo}
              />
            */}
          </div>
        </div>
        <Button
          className={classes.button}
          color="secondary"
          variant="contained"
          disableElevation
          component={LinkScroll}
          to={`${urls.requestReport}#pageStart`}
        >
          Request the reports and implementation blueprint
        </Button>
      </div>
    </Section>
  );
};

export default GetConnectedPolicing;
